import React, { useEffect, useState } from 'react';
import axios from 'axios';

const Settings = () => {
    const [minBookingHours, setMinBookingHours] = useState(24);
    const [maxBookingWeeks, setMaxBookingWeeks] = useState(2);
    const [repeatingHours, setRepeatingHours] = useState('08:00 - 17:00');
    const [repeatingUntil, setRepeatingUntil] = useState('2025-06-06'); // ISO format
    const [questions, setQuestions] = useState([]);
    const [repeatingHoursEnabled, setRepeatingHoursEnabled] = useState(false);
    const [activeEditSection, setActiveEditSection] = useState(null); // 'clientLimits', 'repeatingHours', 'pricing'
    const [startTime, setStartTime] = useState();
    const [endTime, setEndTime] = useState();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const [clientLimitsRes, repeatingHoursRes, questionsRes] = await Promise.all([
                    axios.get('https://bunny-klawz.uc.r.appspot.com/settings/client-limits'),
                    axios.get('https://bunny-klawz.uc.r.appspot.com/settings/repeating-hours'),
                    axios.get('https://bunny-klawz.uc.r.appspot.com/questions'),
                ]);

                // Client Limits
                setMinBookingHours(clientLimitsRes.data.minHours);
                setMaxBookingWeeks(clientLimitsRes.data.maxWeeks);

                // Repeating Hours
                const rh = repeatingHoursRes.data;
                setRepeatingHoursEnabled(rh.enabled);
                setStartTime(parseTime(rh.start));
                setEndTime(parseTime(rh.end));
                setRepeatingUntil(rh.maxDate?.slice(0, 10)); // YYYY-MM-DD
                setQuestions(questionsRes.data);
            } catch (error) {
                console.error("Error loading settings:", error);
            }
        };

        fetchSettings();
    }, []);

    const parseTime = (timeStr) => {
        const [hourMin] = timeStr.split(':');
        const hour = parseInt(hourMin);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const adjustedHour = hour % 12 || 12;
        const minute = timeStr.split(':')[1] || '00';
        return { hour: String(adjustedHour), minute, ampm };
    };

    const to24HrFormat = ({ hour, minute, ampm }) => {
        let hr = parseInt(hour);
        if (ampm === 'PM' && hr !== 12) hr += 12;
        if (ampm === 'AM' && hr === 12) hr = 0;
        return `${String(hr).padStart(2, '0')}:${minute}`;
    };

    const saveClientLimits = async () => {
        try {
            await axios.put('https://bunny-klawz.uc.r.appspot.com/settings/client-limits', {
                minHours: minBookingHours,
                maxWeeks: maxBookingWeeks,
            });
            alert("Client limits saved!");
        } catch (err) {
            console.error("Error saving client limits:", err);
            alert("Failed to save client limits.");
        }
    };

    const saveRepeatingHours = async () => {
        if (!startTime || !endTime) {
            alert("Start time or end time is missing.");
            return;
        }

        try {
            await axios.put('https://bunny-klawz.uc.r.appspot.com/settings/repeating-hours', {
                enabled: repeatingHoursEnabled,
                start: to24HrFormat(startTime),
                end: to24HrFormat(endTime),
                maxDate: repeatingUntil,
            });
            alert("Repeating hours saved!");
        } catch (err) {
            console.error("Error saving repeating hours:", err);
            alert("Failed to save repeating hours.");
        }
    };


    const handleQuestionOptionPriceChange = (qIndex, oIndex, newPrice) => {
        const updated = [...questions];
        updated[qIndex].options[oIndex].price = newPrice;
        setQuestions(updated);
    };


    const handleSave = async () => {
        try {
            for (let qIndex = 0; qIndex < questions.length; qIndex++) {
                const question = questions[qIndex];
                for (let oIndex = 0; oIndex < question.options.length; oIndex++) {
                    const option = question.options[oIndex];

                    // Send price update to backend
                    await axios.put(
                        `https://bunny-klawz.uc.r.appspot.com/questions/${qIndex}/options/${oIndex}/price`,
                        { price: option.price }
                    );
                }
            }

            alert('All prices saved successfully!');
        } catch (error) {
            console.error('Error saving prices:', error);
            alert('Something went wrong while saving prices.');
        }
    };

    return (
        <div className='Settings'>
            {/* Left Side - Settings */}
            <div>
                <div className='ClientLimits'>
                    <div className='SettingsEdit'>
                        <h2 className='SettingHeadersText'>Client Limits</h2>
                        {activeEditSection === 'clientLimits' ? (
                            <button onClick={async () => {
                                await saveClientLimits();
                                setActiveEditSection(null);
                            }}>
                                <img className="DoneIcon" src="/images/DoneIcon.png" alt="Done Icon" />
                                Done
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    if (activeEditSection === null) {
                                        setActiveEditSection('clientLimits');
                                    } else {
                                        alert("Please save before editing another section.");
                                    }
                                }}
                            >
                                <img className="EditIcon" src="/images/EditIcon.png" alt="Edit Icon" />
                                Edit
                            </button>
                        )}
                    </div>

                    <div>
                        <span>Do not let clients book </span>
                        <input
                            type="number"
                            className={`MinBookingHoursInput ${activeEditSection === 'clientLimits' ? 'HoursEditingBox' : ''}`}
                            value={minBookingHours}
                            onChange={(e) => setMinBookingHours(Number(e.target.value))}
                            disabled={activeEditSection !== 'clientLimits'}
                        />
                        <span> hours before the appointment.</span>
                    </div>

                    <div>
                        <span>Do not let clients book </span>
                        <input
                            type="number"
                            className={`MaxBookingWeeksInput ${activeEditSection === 'clientLimits' ? 'HoursEditingBox' : ''}`}
                            value={maxBookingWeeks}
                            onChange={(e) => setMaxBookingWeeks(Number(e.target.value))}
                            disabled={activeEditSection !== 'clientLimits'}
                        />
                        <span> weeks in the future.</span>
                    </div>

                </div>

                <div className='RepeatingHours'>
                    <div className='SettingsEdit'>
                        <h2 className='SettingHeadersText'>Repeating Hours</h2>
                        {activeEditSection === 'repeatingHours' ? (
                            <button onClick={async () => {
                                await saveRepeatingHours();
                                setActiveEditSection(null);
                            }}>
                                <img className="DoneIcon" src="/images/DoneIcon.png" alt="Done Icon" />
                                Done
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    if (activeEditSection === null) {
                                        setActiveEditSection('repeatingHours');
                                    } else {
                                        alert("Please save before editing another section.");
                                    }
                                }}
                            >
                                <img className="EditIcon" src="/images/EditIcon.png" alt="Edit Icon" />
                                Edit
                            </button>
                        )}
                    </div>

                    <label className="custom-checkbox">
                        <input
                            type="checkbox"
                            id="repeatWeekly"
                            checked={repeatingHoursEnabled}
                            onChange={(e) => setRepeatingHoursEnabled(e.target.checked)}
                            disabled={activeEditSection !== 'repeatingHours'}
                        />
                        <span className={`checkmark ${activeEditSection === 'repeatingHours' ? 'HoursEditingBox' : ''}`}></span>
                        Enable Repeating Hours
                    </label>

                    {repeatingHoursEnabled && (
                        <div className='RepeatingHoursInputs'>
                            <div>
                                <label>Start Time</label>
                                <input
                                    type='time'
                                    value={startTime ? to24HrFormat(startTime) : ''}
                                    onChange={(e) => setStartTime(parseTime(e.target.value))}
                                    disabled={activeEditSection !== 'repeatingHours'}
                                />
                            </div>
                            <div>
                                <label>End Time</label>
                                <input
                                    type='time'
                                    value={endTime ? to24HrFormat(endTime) : ''}
                                    onChange={(e) => setEndTime(parseTime(e.target.value))}
                                    disabled={activeEditSection !== 'repeatingHours'}
                                />
                            </div>
                            <div>
                                <label htmlFor="repeatWeekly">Set weekly hours repeating until</label>
                                <input
                                    type="date"
                                    value={repeatingUntil}
                                    onChange={(e) => setRepeatingUntil(e.target.value)}
                                    disabled={activeEditSection !== 'repeatingHours'}
                                />
                            </div>
                        </div>
                    )}
                </div>


            </div>

            {/* Right Side - Pricing Settings */}
            <div>
                <div className='PricingSettings'>
                    <div className='SettingsEdit'>
                        <h2 className='SettingHeadersText'>Pricing Settings</h2>
                        {activeEditSection === 'pricing' ? (
                            <button onClick={() => { setActiveEditSection(null); handleSave(); }}>
                                <img className="DoneIcon" src="/images/DoneIcon.png" alt="Done Icon" />
                                Save
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    if (activeEditSection === null) {
                                        setActiveEditSection('pricing');
                                    } else {
                                        alert("Please save before editing another section.");
                                    }
                                }}
                            >
                                <img className="EditIcon" src="/images/EditIcon.png" alt="Edit Icon" />
                                Edit
                            </button>
                        )}
                    </div>

                    <table>
                        <thead>
                            <tr>
                                <th>Service</th>
                                <th>Cost ($)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                questions.map((question, qIndex) =>
                                    question.options.map((option, index) => (
                                        qIndex === 0 && index === 2 ? (<></>) : (
                                            <tr key={`${qIndex}-${index}`}>
                                                {qIndex === 1 ? (<td>{`Gel-X Extension ` + option.label}</td>) : option.label === 'French Tips (no additional design)' ? (<td>French Tips</td>) : (<td>{option.label}</td>)}
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={option.price}
                                                        onChange={(e) => handleQuestionOptionPriceChange(qIndex, index, Number(e.target.value))}
                                                        disabled={activeEditSection !== 'pricing'}
                                                    />
                                                </td>
                                            </tr>
                                        )

                                    ))
                                )
                            }
                        </tbody>
                    </table>

                    {/* <div className='SavePricingSettings'>
                    <button onClick={handleSave} disabled={activeEditSection !== 'pricing'}>
                        Save
                    </button>
                </div> */}
                </div>
            </div>
        </div>
    );
};

export default Settings;
